import { DialogueType } from "@/features/streaming/streamingRoom/chatroom/config";
import { ChatGiftItem } from "./chat";

export type BaseInfoType = {
    title: string;
    type: StreamingType;
    diamond: number;
    filter_status: FilterStatusType;
    filter_settings: {
        beauty_settings: {
            eye_bag: number;
            nasolabial_fold: number;
            white_teeth: number;
            blush: number;
            lipstick: number;
            lipstick_color: number;
            lipstick_gloss: number;
            lipstick_brightness: number;
            rosiness: number;
            bright_eyes: number;
            wrinkle_removal: number;
            dark_circle_removal: number;
        };
        face_shaping: {
            cheekbone: number;
            face_slimming: number;
            face_thinning: number;
            face_length: number;
            chin_length: number;
            chin_slimming: number;
            jaw_slimming: number;
            eye_enlargement: number;
            eye_corner1: number;
            eye_distance: number;
            eye_distance_widening: number;
            eye_corner2: number;
            eye_height: number;
            nose_slimming: number;
            nose_wing: number;
            nose_length: number;
            nose_tip_length: number;
            lip_width: number;
            lip_size: number;
            lip_height: number;
            philtrum: number;
            hairline: number;
            smile: number;
        };
        body_shaping: {
            overall: number;
            long_legs: number;
            small_head: number;
            slim_legs: number;
            neck: number;
            slim_waist: number;
            bust_enhancement: number;
            arms: number;
        };
    };
};

export interface SettingInfoType extends BaseInfoType {
    id: string;
    userId: number;
    labels: LabelType[];
    cover: string;
    label_ids?: string[];
}

export interface UpdateSettingInfoType extends BaseInfoType {
    label_ids: string[];
}

export type CommandType = {
    id?: string;
    content: string;
    diamond: number | string;
};
export interface CommandRecordType extends CommandType {
    user_id: string;
    nickname: string;
    avatar: string;
    status: 0 | 1 | 2;
    handleClick?: ((commandId: string) => Promise<any>) | null;
    text?: string;
    className?: string;
}

export type LabelType = {
    key: string;
    name: string;
};

export type UpdateCoverType = {
    cover: string;
};

export enum StreamingType {
    Public = 1, // 免費
    Private = 2 // 收費
}
export type FilterStatusType = 0 | 1; // 0：未啟用濾鏡 1：啟用濾鏡"

export type BeautyParamsCategory = 'beauty_settings' | 'face_shaping' | 'body_shaping';

export type FaceBeautyParamsKey =
    | 'eye_bag'
    | 'nasolabial_fold'
    | 'white_teeth'
    | 'blush'
    | 'lipstick'
    | 'lipstick_color'
    | 'lipstick_gloss'
    | 'lipstick_brightness'
    | 'rosiness'
    | 'bright_eyes'
    | 'wrinkle_removal'
    | 'dark_circle_removal';

export type FaceShapeParamsKey =
    | 'cheekbone'
    | 'face_slimming'
    | 'face_thinning'
    | 'face_length'
    | 'chin_length'
    | 'chin_slimming'
    | 'jaw_slimming'
    | 'eye_enlargement'
    | 'eye_corner1'
    | 'eye_distance'
    | 'eye_distance_widening'
    | 'eye_corner2'
    | 'eye_height'
    | 'nose_slimming'
    | 'nose_wing'
    | 'nose_length'
    | 'nose_tip_length'
    | 'lip_width'
    | 'lip_size'
    | 'lip_height'
    | 'philtrum'
    | 'hairline'
    | 'smile';

export type BodyShapeParamsKey =
    | 'overall'
    | 'long_legs'
    | 'small_head'
    | 'slim_legs'
    | 'neck'
    | 'slim_waist'
    | 'bust_enhancement'
    | 'arms';

export type BeautyParamsKey = FaceBeautyParamsKey | FaceShapeParamsKey | BodyShapeParamsKey;

export type BeautyParamsDraft = {
    beauty_settings: {
        [key in FaceBeautyParamsKey]: number;
    };
    face_shaping: {
        [key in FaceShapeParamsKey]: number;
    };
    body_shaping: {
        [key in BodyShapeParamsKey]: number;
    };
};

export type StreamingInfo = {
    live_id: string;
    user_id: string;
    heat: number;
    cover: string;
    type: StreamingType;
    diamond: number;
    push_url: string;
    pull_url: {
        flv: string;
        m3u8: string;
    };
    chat_token: string;
    isPlaying?: boolean;
    status: number;
};

export interface StreamingInfoFromApi {
    live_id: string;
    user_id: string;
    heat: number;
    cover: string;
    type: StreamingType;
    diamond: number;
    status: number;
};

export interface ScrollStreamingInfo extends StreamingInfo {
    index?: number;
    transform?: number;
}

export interface StartStreamingInfoFromApi {
    live_id: string;
    cover: string;
    type: StreamingType;
    push_url: string;
    diamond: number;
    chat_token: string;
};

export interface EnterStreamingInfoFromApi extends StreamingInfoFromApi {
    pull_url: {
        m3u8: string;
        flv: string;
    }
    chat_token: string;
    announcement: string;
}

export interface FetchSDPResponse {
    code: number;
    message: string;
    remoteSdp: {
        type: string;
        sdp: string;
    };
}

export interface LocalSdpType {
    type: 'offer' | 'answer';
    sdp: string;
}

export enum StreamRoomState {
    Preview = "preview",
    Cp = "cp",
    User = "user"
}

export interface GiftCategory {
    id: string;
    name: string;
}

export interface ChatSayBody {
    msg: {
        type: string;
        content: string;
    }
    nickname: string;
    avatar: string;
}

export interface StreamingChatBase {
    live_id: string;
    user_id: string;
    nickname: string;
    avatar: string;
}

export interface JoinedMessage {
    user_id: string;
    nickname: string;
    avatar: string;
}

export interface JoinedMessageWithType extends JoinedMessage {
    msgType: DialogueType.System
}

export interface TextMessage extends StreamingChatBase {
    type: "text";
    msg: string;
    ts: number; // timestamp
}

export interface TextMessageWithType extends TextMessage {
    msgType: DialogueType.Text | DialogueType.CpText
}

export interface GiftMessage extends StreamingChatBase {
    gift: {
        id: string;
        price: number;
        name: string;
        image: string;
    }
    uuid: string;
}

export interface GiftMessageWithType extends GiftMessage {
    msgType: DialogueType.Gift
}

export interface BuyCommandMessage extends StreamingChatBase {
    command: {
        id: string;
        content: string;
        diamond?: number;
    }
}

export interface CommandMessageWithType extends BuyCommandMessage {
    msgType: DialogueType.BuyCommand | DialogueType.ExecuteCommand
}

export type ChatMessage = JoinedMessageWithType | TextMessageWithType | CommandMessageWithType;

export interface StreamerInfo {
    userId: string;
    nickname: string;
    avatar: string;
    isFollowing: boolean;
    account: string;
}

export enum AnnouncementPinnedState {
    False = 0,
    Ticker = 1,
    True = 2
}

export interface Announcement {
    announcement: string;
    pinned: AnnouncementPinnedState;
}

export enum RoomChangeState {
    Init = "init", // 啥也沒做
    Waiting = "waiting", // user 系統訊息：等1分鐘
    Changing = "changing", // user 開始讀秒切換
    End = "end" // 已關
}

export enum MuteStatus {
    Muted = 1,
    Unmuted = 0
}

export interface MuteStatusFromApi {
    mute_status: MuteStatus;
    user_id: string;
    nickname: string;
    avatar: string;
    account: string;
}

export const IGNORE_CHANGE_ROOM = "ignore_change_room";

export interface PaymentInfo {
    payment_info: {
        is_paid: boolean;
        last_payment_time: string;
        remaining_seconds: number;
    }
    pull_url: {
        flv: string;
        m3u8: string;
    }
    success: number;
}
